/*
 * Turns a set of selected template-slide ids into slides in the deck. Shared
 * by Slide Templates (slide-templates/services/slideTemplateInsert.ts) and
 * Presentation Templates (presentation-templates/services/
 * presentationTemplateInsert.ts) — both features fetch each entry's own
 * source `.pptx` (a directory-SAS link, possibly holding several other
 * templates' slides too) and carve out just the slides the user picked, with
 * no insertable payload ever living in either feature's own manifest.
 *
 * MUST only ever run in the task pane: PowerPoint.run() is unavailable inside
 * an Office Dialog window (insertSlideFromBase64 throws there by design).
 *
 * ONE FETCH + ONE INSERT PER SOURCE FILE, NOT PER SLIDE
 * A naive per-slide loop (fetch the source, carve out one slide, insert,
 * repeat) re-downloads the SAME source `.pptx` once per slide and pays a full
 * PowerPoint.run() round trip per slide too — selecting many entries from the
 * same source file would mean that many downloads and that many separate
 * Office.js calls. This instead groups the selection into runs of consecutive
 * same-source entries (preserving overall order), fetches each source
 * exactly once, and hands `buildSubDeckBase64` the WHOLE run's slide
 * positions at once — `insertSlidesFromBase64` (see
 * create-slide/hooks/slideInsert.ts) already natively inserts every slide in
 * a base64 package in a single call, so this only needed the package to
 * contain more than one slide to begin with.
 */

import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";
import { insertSlideFromBase64 } from "../../create-slide/hooks/slideInsert";
import { sasVersionHeaders } from "./blobImageUpload";
import { getErrorMessage } from "./connectorErrors";

/** The fields `insertTemplateSlides` needs from a caller's own template/slide
 *  type — Slide Templates' `SlideTemplate` and Presentation Templates'
 *  `PresentationTemplateSlide` both satisfy this shape already. */
export interface InsertableTemplateSlide {
  readonly id: string;
  readonly insertable: boolean;
  readonly sourceFileUrl: string | null;
  readonly slideIndex: number | null;
}

export interface TemplateSlideInsertMessages {
  /** Thrown when nothing in the selection is insertable. */
  readonly noInsertableSelection: string;
  /** Prefixed to a source-file download failure's status/body. */
  readonly sourceDownloadFailed: string;
}

export interface TemplateSlideInsertTelemetry {
  readonly log: (reason: string, message: string) => void;
  /** Feature-specific prefix for this call's own telemetry reasons, e.g.
   *  "slide-templates" or "presentation-templates". */
  readonly reasonPrefix: string;
}

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000; // stay under the arg-count limit of String.fromCodePoint
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCodePoint(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Build a base64 `.pptx` containing the slides at `slidePositions` (0-based,
 * in presentation order), in the given order, from `originalDeck`.
 */
function buildSubDeckBase64(originalDeck: ArrayBuffer, slidePositions: readonly number[]): string {
  if (slidePositions.length === 0) throw new Error("No slide positions were given to build a sub-deck from.");

  const files = unzipSync(new Uint8Array(originalDeck));
  const presBytes = files["ppt/presentation.xml"];
  if (!presBytes) throw new Error("The original deck is missing ppt/presentation.xml.");

  const xml = strFromU8(presBytes);
  const listBody = /<p:sldIdLst>([\s\S]*?)<\/p:sldIdLst>/.exec(xml)?.[1];
  if (listBody == null) throw new Error("The original deck has no slide list.");

  const sldIds = listBody.match(/<p:sldId\b[^>]*?\/>/g) ?? [];
  const selected = slidePositions.map((position) => {
    const target = sldIds[position];
    if (!target) throw new Error(`Original slide ${position + 1} was not found.`);
    return target;
  });

  const trimmed = xml.replace(/<p:sldIdLst>[\s\S]*?<\/p:sldIdLst>/, `<p:sldIdLst>${selected.join("")}</p:sldIdLst>`);
  files["ppt/presentation.xml"] = strToU8(trimmed);
  return uint8ToBase64(zipSync(files));
}

/** One consecutive run of selected entries that all share a `sourceFileUrl`. */
interface SourceRun<T> {
  readonly sourceFileUrl: string;
  readonly templates: T[];
}

/**
 * Groups `insertable` into runs of consecutive entries sharing one
 * `sourceFileUrl`, preserving overall order.
 *
 * Assumes every entry already passed `isFullyInsertable` (non-null
 * `sourceFileUrl`/`slideIndex`); callers filter the selection before this runs.
 */
function groupBySourceFile<T extends InsertableTemplateSlide>(insertable: readonly T[]): SourceRun<T>[] {
  const runs: SourceRun<T>[] = [];
  for (const template of insertable) {
    const sourceFileUrl = template.sourceFileUrl!;
    const last = runs[runs.length - 1];
    if (last?.sourceFileUrl === sourceFileUrl) {
      last.templates.push(template);
    } else {
      runs.push({ sourceFileUrl, templates: [template] });
    }
  }
  return runs;
}

async function fetchSourceBuffer(
  sourceFileUrl: string,
  messages: TemplateSlideInsertMessages,
  telemetry: TemplateSlideInsertTelemetry,
): Promise<ArrayBuffer> {
  const res = await fetch(sourceFileUrl, {
    method: "GET",
    // Same user-delegation-SAS requirement as the manifest download — see
    // shared/services/blobImageUpload.ts's SAS_VERSION_HEADER doc.
    headers: sasVersionHeaders(sourceFileUrl, telemetry.log, `${telemetry.reasonPrefix}-source-sas-version-parse`),
  });
  if (!res.ok) {
    const errText = await res.text().catch((error: unknown) => {
      telemetry.log(`${telemetry.reasonPrefix}-source-body-read`, getErrorMessage(error, "Failed to read source-file error body"));
      return "";
    });
    throw new Error(`${messages.sourceDownloadFailed} (${res.status} ${errText})`);
  }
  return res.arrayBuffer();
}

/**
 * `insertable` (see each feature's own manifest type) is trusted for the
 * normal manifest path, but re-checked here for real — a stale/tampered
 * selection must not reach `groupBySourceFile`'s non-null assertions with a
 * null field.
 */
function isFullyInsertable(template: InsertableTemplateSlide): boolean {
  return template.insertable && template.sourceFileUrl !== null && template.slideIndex !== null;
}

/**
 * Inserts the selected templates, in the order `templates` lists them.
 *
 * Order comes from `templates`, not from `selectedIds`, so the deck matches
 * what the grid showed rather than the order the user happened to click. Runs
 * are inserted sequentially (concurrent insertSlidesFromBase64 calls would
 * race for the same insertion point), but each run is one batched call
 * covering every entry in it — see `groupBySourceFile`.
 *
 * Throws when nothing could be inserted (so the caller can show one clear
 * error) and propagates the first run's failure otherwise, leaving any
 * already-inserted runs in place — a partial insert is recoverable by the
 * user, a silent no-op is not.
 *
 * @returns how many slides were inserted.
 */
export async function insertTemplateSlides<T extends InsertableTemplateSlide>(
  templates: readonly T[],
  selectedIds: readonly string[],
  messages: TemplateSlideInsertMessages,
  telemetry: TemplateSlideInsertTelemetry,
): Promise<number> {
  const wanted = new Set(selectedIds);
  const insertable = templates.filter((template) => wanted.has(template.id) && isFullyInsertable(template));
  if (insertable.length === 0) throw new Error(messages.noInsertableSelection);

  let inserted = 0;
  for (const run of groupBySourceFile(insertable)) {
    const buffer = await fetchSourceBuffer(run.sourceFileUrl, messages, telemetry);
    // slideIndex is 1-based (presentation order); buildSubDeckBase64 wants
    // 0-based positions into the same order.
    const positions = run.templates.map((template) => template.slideIndex! - 1);
    const base64 = buildSubDeckBase64(buffer, positions);
    await insertSlideFromBase64(base64);
    inserted += run.templates.length;
  }
  return inserted;
}
